/// <amd-module name="Test/TestResultsGrid/Store/GroupByManager" />
import { TestResultsGroupPivots, FilterByFields, TestOutcomeEnum } from 'Test/common-util/Common';
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { TCMContractsConverter } from "Test/common-util/Common.Utils";
import { equals } from "VSS/Core/Util/String";
import { Constants, GroupByPivotHelper } from "Test/TestResultsGrid/TestResultGridHelper";

export interface IGroupSummary {
    groupSummaryData: { [key: string]: TCMContracts.TestResultsDetailsForGroup };
    summaryLoaded: boolean;
}

export class TestResultsGroupByManager {
    constructor() {
        this.resetCache();
    }

    public resetCache() {
        this._cachedGroupKeyToResultsForGroups = {};
    }

    public resetShallowFetchedDataInGroupByManager() {
        let groupSummary: IGroupSummary = this._cachedGroupKeyToResultsForGroups[TestResultsGroupPivots.Group_By_Test_Run];
        if(groupSummary && groupSummary.summaryLoaded)
        {
            Object.keys(groupSummary.groupSummaryData).forEach((groupValue: string) => {
                groupSummary.groupSummaryData[groupValue].results = [];
            });
        }
        delete this._cachedGroupKeyToResultsForGroups[TestResultsGroupPivots.Group_By_Priority]; 
        delete this._cachedGroupKeyToResultsForGroups[TestResultsGroupPivots.Group_By_Owner];
        delete this._cachedGroupKeyToResultsForGroups[TestResultsGroupPivots.Group_By_None];
        delete this._cachedGroupKeyToResultsForGroups[TestResultsGroupPivots.Group_By_Container];
    }

    public populateShallowResult(result: TCMContracts.ShallowTestCaseResult) {
        const testCaseResult: TCMContracts.TestCaseResult = TCMContractsConverter.convertShallowTestResultToTestCaseResult(result);

        this._populateGroupedResultsForEachGroupBy(TestResultsGroupPivots.Group_By_Container, result.automatedTestStorage, result, testCaseResult);
        this._populateGroupedResultsForEachGroupBy(TestResultsGroupPivots.Group_By_Owner, result.owner, result, testCaseResult);
        if (result.priority != null) {
            this._populateGroupedResultsForEachGroupBy(TestResultsGroupPivots.Group_By_Priority, result.priority.toString(), result, testCaseResult);
        }
        if (result.runId != null) {
            this._populateGroupedResultsForEachGroupBy(TestResultsGroupPivots.Group_By_Test_Run, result.runId.toString(), result, testCaseResult);
        }
        this._populateGroupedResultsForEachGroupBy(TestResultsGroupPivots.Group_By_None, "", result, testCaseResult);
    }

    public getGroupByResults(groupByField: string): TCMContracts.TestResultsDetails {
        const groupSummaryMap = this._cachedGroupKeyToResultsForGroups[groupByField]
            ? this._cachedGroupKeyToResultsForGroups[groupByField].groupSummaryData
            : null;

        if (groupSummaryMap) {
            let resultsForGroupWithoutTag = Object.keys(groupSummaryMap).map(key => groupSummaryMap[key]);
            if (groupByField === TestResultsGroupPivots.Group_By_Test_Run && !!this._cacheTestTagSummary) {
                resultsForGroupWithoutTag.forEach(element => {
                    const runId: number = GroupByPivotHelper.getRunId(element, TestResultsGroupPivots.Group_By_Test_Run);
                    if (!!this._cacheTestTagSummary.tagsGroupByTestArtifact[runId]) {
                        element.tags = this._cacheTestTagSummary.tagsGroupByTestArtifact[runId].map(tag => tag.name);
                    }
                });
            }
            return {
                groupByField: groupByField,
                resultsForGroup: resultsForGroupWithoutTag
            } as TCMContracts.TestResultsDetails;
        } else {
            return {
                groupByField: groupByField,
                resultsForGroup: []
            } as TCMContracts.TestResultsDetails;
        }
    }

    public isGroupBySummaryLoaded(groupByField: string) {
        return this._cachedGroupKeyToResultsForGroups[groupByField] &&
            this._cachedGroupKeyToResultsForGroups[groupByField].summaryLoaded;
    }

    public populateGroupedResults(groupedResults: TCMContracts.TestResultsDetails) {
        const allResultsLoaded: boolean = equals(groupedResults.groupByField, TestResultsGroupPivots.Group_By_Requirement) || equals(groupedResults.groupByField, TestResultsGroupPivots.Group_By_Test_Suite);

        if (!this._cachedGroupKeyToResultsForGroups.hasOwnProperty(groupedResults.groupByField)) {
            this._cachedGroupKeyToResultsForGroups[groupedResults.groupByField] = {
                summaryLoaded: true,
                groupSummaryData: {}
            };
        } else {
            this._cachedGroupKeyToResultsForGroups[groupedResults.groupByField].summaryLoaded = true;
        }

        groupedResults.resultsForGroup.forEach((groupedResult: TCMContracts.TestResultsDetailsForGroup) => {
            const groupSummaryMap = this._cachedGroupKeyToResultsForGroups[groupedResults.groupByField].groupSummaryData;
            const groupById = this._getGroupByIdFromGroupedResults(groupedResults.groupByField as TestResultsGroupPivots, groupedResult);

            if (groupSummaryMap.hasOwnProperty(groupById)) {
                groupSummaryMap[groupById].groupByValue = groupedResult.groupByValue;
                groupSummaryMap[groupById].resultsCountByOutcome = groupedResult.resultsCountByOutcome;
                if (allResultsLoaded) {
                    groupSummaryMap[groupById].results = groupedResult.results;
                }
            } else {
                let resultsForGroup: TCMContracts.TestResultsDetailsForGroup = {
                    groupByValue: groupedResult.groupByValue,
                    results: allResultsLoaded ? groupedResult.results : [],
                    resultsCountByOutcome: groupedResult.resultsCountByOutcome,
                    tags: groupedResult.tags
                };
                groupSummaryMap[groupById] = resultsForGroup;
            }
        });
    }

    public updateTagsForTestRun(testTagSummary: TCMContracts.TestTagSummary) {
        this._cacheTestTagSummary = testTagSummary;
    }

    public shouldCalculateTotalDuration(groupByField: TestResultsGroupPivots) {
        return groupByField === TestResultsGroupPivots.Group_By_Requirement ||
            groupByField === TestResultsGroupPivots.Group_By_Test_Run ||
            groupByField === TestResultsGroupPivots.Group_By_Test_Suite;
    }

    private _populateGroupedResultsForEachGroupBy(groupByField: TestResultsGroupPivots, groupByValue: string, result: TCMContracts.ShallowTestCaseResult, testCaseResult: TCMContracts.TestCaseResult) {
        if (!this._cachedGroupKeyToResultsForGroups.hasOwnProperty(groupByField)) {
            this._cachedGroupKeyToResultsForGroups[groupByField] = {
                summaryLoaded: true,
                groupSummaryData: {}
            };
        }
        const groupSummaryMap = this._cachedGroupKeyToResultsForGroups[groupByField].groupSummaryData;
        const outcome: number = (<any>TestOutcomeEnum)[result.outcome];

        if (groupSummaryMap.hasOwnProperty(groupByValue)) {
            groupSummaryMap[groupByValue].results.push(testCaseResult);
            if (this._shouldCalculateTotalResultCount(groupByField)) {
                if (groupSummaryMap[groupByValue].resultsCountByOutcome.hasOwnProperty(outcome)) {
                    groupSummaryMap[groupByValue].resultsCountByOutcome[outcome].count += 1;
                } else {
                    const aggregatedResults: TCMContracts.AggregatedResultsByOutcome = {
                        count: 1,
                        duration: 0,
                        outcome: outcome,
                        rerunResultCount: 0
                    } as TCMContracts.AggregatedResultsByOutcome;

                    groupSummaryMap[groupByValue].resultsCountByOutcome[outcome] = aggregatedResults;
                }
            }
        }
        else {
            let resultsForGroup: TCMContracts.TestResultsDetailsForGroup;
            if (groupByField === TestResultsGroupPivots.Group_By_Test_Run) {
                resultsForGroup = {
                    groupByValue:
                        {
                            id: groupByValue
                        },
                    results: [],
                    resultsCountByOutcome: [],
                    tags: []
                };
            }
            else {
                resultsForGroup = {
                    groupByValue: groupByValue,
                    results: [],
                    resultsCountByOutcome: [],
                    tags: []
                };
            }

            resultsForGroup.results.push(testCaseResult);
            if (this._shouldCalculateTotalResultCount(groupByField)) {
                if (resultsForGroup.resultsCountByOutcome.hasOwnProperty(outcome)) {
                    resultsForGroup.resultsCountByOutcome[outcome].count += 1;
                } else {
                    const aggregatedResults: TCMContracts.AggregatedResultsByOutcome = {
                        count: 1,
                        duration: 0,
                        outcome: outcome,
                        rerunResultCount: 0
                    } as TCMContracts.AggregatedResultsByOutcome;

                    resultsForGroup.resultsCountByOutcome[outcome] = aggregatedResults;
                }
            }
            groupSummaryMap[groupByValue] = resultsForGroup;
        }
    }

    private _getGroupByIdFromGroupedResults(groupByField: TestResultsGroupPivots, groupedResult: TCMContracts.TestResultsDetailsForGroup): string {
        let groupById: string = "";
        switch (groupByField) {
            case TestResultsGroupPivots.Group_By_Test_Run:
                const testRun = <TCMContracts.TestRun>groupedResult.groupByValue;
                groupById = testRun.id.toString();
                break;

            case TestResultsGroupPivots.Group_By_Test_Suite:
                const testSuite = <TCMContracts.TestSuite>groupedResult.groupByValue;
                groupById = testSuite.id.toString();
                break;

            case TestResultsGroupPivots.Group_By_Requirement:
                const workItem = <TCMContracts.WorkItemReference>groupedResult.groupByValue;
                const workItemId: number = parseInt(workItem.id);
                groupById = workItem.id;
                break;

            case TestResultsGroupPivots.Group_By_Container:
            case TestResultsGroupPivots.Group_By_Priority:
            case TestResultsGroupPivots.Group_By_None:
            case TestResultsGroupPivots.Group_By_Owner:
                groupById = <string>groupedResult.groupByValue;
                break;
        }
        return groupById;
    }

    private _shouldCalculateTotalResultCount(groupByField: TestResultsGroupPivots) {
        return groupByField === TestResultsGroupPivots.Group_By_Container ||
            groupByField === TestResultsGroupPivots.Group_By_Owner ||
            groupByField === TestResultsGroupPivots.Group_By_Priority;
    }

    private _cachedGroupKeyToResultsForGroups: { [key: string]: IGroupSummary };
    private _cacheTestTagSummary: TCMContracts.TestTagSummary;
}
